import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, signInWithEmailAndPassword, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { getFirestore, doc, getDoc, collection, getDocs, setDoc } from 'firebase/firestore';
import './AdminLogin.css';

const AdminLoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const auth = getAuth();

  const consumeRedirectAfterLogin = () => {
    const redirect = localStorage.getItem('redirectAfterLogin');
    if (redirect) {
      localStorage.removeItem('redirectAfterLogin');
      return redirect;
    }
    return null;
  };

  // Only check auth state on mount (not realtime) to avoid conflicts during sign-in
  useEffect(() => {
    if (auth.currentUser) {
      const redirect = consumeRedirectAfterLogin();
      navigate(redirect || '/admin/dashboard', { replace: true });
    }
  }, []);

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError('');

    try {
      const provider = new GoogleAuthProvider();
      provider.addScope('email');
      provider.addScope('profile');

      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      console.log('Google sign-in success:', user.uid, user.email);

      // Load user data and locations
      const db = getFirestore();
      console.log('Checking Firestore for user:', user.uid);
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      console.log('User doc exists:', userDoc.exists());

      if (!userDoc.exists()) {
        // Google user doesn't exist in our users collection
        console.log('User doc not found for Google sign-in');
        await auth.signOut();
        setError('No account found for this Google account. Please sign up to create one.');
        setLoading(false);
        return;
      }

      const userData = userDoc.data();
      const otpVerified = userData?.signupOtpVerified;
      const otpRequired = userData?.signupOtpRequired === true || otpVerified === false;

      if (otpRequired && otpVerified !== true) {
        navigate('/admin/signup/otp');
        return;
      }

      // If no selected location, set the first one
      if (!userData.selectedLocationId) {
        const locationsSnapshot = await getDocs(collection(db, 'users', user.uid, 'locations'));
        if (!locationsSnapshot.empty) {
          const firstLocation = locationsSnapshot.docs[0];
          await setDoc(doc(db, 'users', user.uid), {
            selectedLocationId: firstLocation.id
          }, { merge: true });
        }
      }

      // Check onboarding status and redirect accordingly
      const stripeAccountId = userData?.stripeAccountId;
      const onboardingComplete = userData?.stripeOnboardingComplete === true ||
        (userData?.stripeChargesEnabled === true && userData?.stripePayoutsEnabled === true && userData?.stripeDetailsSubmitted === true);
      const hasPlan = userData?.activeSubscriptionStatus === 'active';

      console.log('[Login] Redirect check:', { stripeAccountId, onboardingComplete, hasPlan });

      const pendingRedirect = consumeRedirectAfterLogin();
      if (pendingRedirect) {
        navigate(pendingRedirect);
        return;
      }

      if (!stripeAccountId || !onboardingComplete) {
        navigate('/admin/onboarding');
      } else if (!hasPlan) {
        navigate('/admin/plans');
      } else {
        navigate('/admin/dashboard');
      }
    } catch (err) {
      console.error('Google sign-in error:', err);
      if (err.code === 'auth/popup-closed-by-user') {
        setError('Sign-in cancelled. Please try again.');
      } else if (err.code === 'auth/popup-blocked') {
        setError('Popup was blocked by your browser. Please allow popups for this site.');
      } else if (err.code === 'auth/unauthorized-domain') {
        setError('This domain is not authorized for Google Sign-In. Please contact support.');
      } else {
        setError(err.message || 'Google sign-in failed. Please try again.');
      }
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Please enter email and password');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await signInWithEmailAndPassword(auth, email.trim().toLowerCase(), password);
      const user = result.user;
      
      // Load user data and locations
      const db = getFirestore();
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const otpVerified = userData?.signupOtpVerified;
        const otpRequired = userData?.signupOtpRequired === true || otpVerified === false;

        if (otpRequired && otpVerified !== true) {
          navigate('/admin/signup/otp');
          return;
        }
        
        // If no selected location, set the first one
        if (!userData.selectedLocationId) {
          const locationsSnapshot = await getDocs(collection(db, 'users', user.uid, 'locations'));
          if (!locationsSnapshot.empty) {
            const firstLocation = locationsSnapshot.docs[0];
            await setDoc(doc(db, 'users', user.uid), {
              selectedLocationId: firstLocation.id
            }, { merge: true });
          }
        }

        // Check onboarding status and redirect accordingly
        const stripeAccountId = userData?.stripeAccountId;
        const onboardingComplete = userData?.stripeOnboardingComplete === true ||
          (userData?.stripeChargesEnabled === true && userData?.stripePayoutsEnabled === true && userData?.stripeDetailsSubmitted === true);
        const hasPlan = userData?.activeSubscriptionStatus === 'active';

        console.log('[Login] Redirect check:', { stripeAccountId, onboardingComplete, hasPlan });

        const pendingRedirect = consumeRedirectAfterLogin();
        if (pendingRedirect) {
          navigate(pendingRedirect);
          return;
        }

        if (!stripeAccountId || !onboardingComplete) {
          navigate('/admin/onboarding');
        } else if (!hasPlan) {
          navigate('/admin/plans');
        } else {
          navigate('/admin/dashboard');
        }
      } else {
        // Should not happen — user authenticated but no Firestore record
        navigate('/admin/dashboard');
      }
    } catch (err) {
      setLoading(false);
      if (err.code === 'auth/multi-factor-auth-required') {
        sessionStorage.setItem('mfa.email', email.trim().toLowerCase());
        sessionStorage.setItem('mfa.password', password);
        navigate('/admin/mfa-verify');
      } else if (err.code === 'auth/invalid-email') {
        setError('Please enter a valid email address');
      } else if (err.code === 'auth/user-not-found') {
        setError('No user found with this email');
      } else if (err.code === 'auth/wrong-password') {
        setError('Incorrect password');
      } else if (err.code === 'auth/invalid-credential') {
        setError('Invalid email or password');
      } else {
        setError(err.message || 'Login failed');
      }
    }
  };

  return (
    <div className="admin-login-page">
      {/* Ingredient sketches background */}
      <div className="ff-hero-ingredients">
        <div className="ff-ingredient tomato"></div>
        <div className="ff-ingredient pepper"></div>
        <div className="ff-ingredient mushroom"></div>
        <div className="ff-ingredient leaf"></div>
        <div className="ff-ingredient onion"></div>
        <div className="ff-ingredient carrot"></div>
        <div className="ff-ingredient garlic"></div>
        <div className="ff-ingredient broccoli"></div>
        <div className="ff-ingredient banana"></div>
        <div className="ff-ingredient bread"></div>
        <div className="ff-ingredient egg"></div>
        <div className="ff-ingredient sausage"></div>
        <div className="ff-ingredient cheese"></div>
        <div className="ff-ingredient lemon"></div>
        <div className="ff-ingredient strawberry"></div>
      </div>

      <div className="login-box">
        <div className="card card-primary card-outline">
          <div className="card-body login-card-body">
            <div className="text-center mb-4">
              <img src="/fodyfee_logo.png" alt="FodyFee" style={{ width: '80px', height: '80px' }} />
              <h3 className="login-heading">FodyFee Admin</h3>
              <p className="login-subheading">Sign in to manage your account</p>
            </div>

            {error && (
              <div className="alert alert-danger">
                {error}
              </div>
            )}

            {/* Google Sign In Button */}
            <button
              type="button"
              className="btn btn-outline-dark btn-block mb-3"
              onClick={handleGoogleSignIn}
              disabled={loading}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                borderRadius: '8px',
                padding: '10px',
                fontWeight: 500,
              }}
            >
              <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.64 9.2c0-.63-.06-1.25-.16-1.84H9v3.49h4.84c-.21 1.15-.84 2.12-1.79 2.77v2.31h2.9c1.69-1.56 2.69-3.86 2.69-6.73z" fill="#4285F4"/>
                <path d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.9-2.31c-.8.54-1.83.86-3.06.86-2.36 0-4.36-1.59-5.07-3.74H.96v2.54C2.45 15.98 5.48 18 9 18z" fill="#34A853"/>
                <path d="M3.93 10.63c-.18-.54-.29-1.12-.29-1.72s.11-1.18.29-1.72V4.65H.96C.35 6.1 0 7.72 0 9.41c0 1.69.35 3.31.96 4.76l2.97-2.54z" fill="#FBBC05"/>
                <path d="M9 3.58c1.32 0 2.51.46 3.44 1.35l2.58-2.58C12.47.91 10.43 0 9 0 5.48 0 2.45 2.02.96 4.65l2.97 2.54c.71-2.15 2.71-3.74 5.07-3.74z" fill="#EA4335"/>
              </svg>
              {loading ? 'Signing in...' : 'Sign in with Google'}
            </button>

            <div className="text-center mb-3">
              <span style={{ color: '#999', fontSize: '0.85rem' }}>— or sign in with email —</span>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="input-group mb-3">
                <input
                  type="email"
                  className="form-control"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                />
                <div className="input-group-append">
                  <div className="input-group-text">
                    <span className="fas fa-user"></span>
                  </div>
                </div>
              </div>

              <div className="input-group mb-3">
                <input
                  type={showPassword ? 'text' : 'password'}
                  className="form-control"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                />
                <div className="input-group-append">
                  <div className="input-group-text">
                    <span 
                      className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}
                      onClick={() => setShowPassword(!showPassword)}
                      style={{ cursor: 'pointer' }}
                    ></span>
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-12">
                  <button 
                    type="submit" 
                    className="btn btn-primary btn-block"
                    disabled={loading}
                  >
                    {loading ? 'Signing in...' : 'Sign In'}
                  </button>
                </div>
              </div>

              <div className="row mt-3">
                <div className="col-12 text-center">
                  <a 
                    href="/admin/forgot-password" 
                    className="forgot-password-link"
                  >
                    Forgot Password?
                  </a>
                </div>
              </div>

              <div className="row mt-3">
                <div className="col-12 text-center">
                  <p className="signup-link">
                    Don't have an account?{' '}
                    <a 
                      href="/admin/signup" 
                      className="signup-link-a"
                    >
                      Sign up
                    </a>
                  </p>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;
